"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import {
  GraduationCap,
  Sparkles,
  ArrowRight,
  Check,
  CreditCard,
  ShieldCheck,
  MonitorPlay,
  Clock,
  MapPin,
  CalendarX,
  Star,
  Gift,
  PlayCircle,
  BookOpen,
  ChevronDown,
  X,
} from "lucide-react";
import Navbar from "@/components/sections/Navbar";
import Footer from "@/components/sections/Footer";
import PromoCountdown from "@/components/ui/PromoCountdown";
import StickyCtaBar from "@/components/landing/StickyCtaBar";
import { formatPriceLabel } from "@/lib/course-price-label";

interface LandingCourse {
  id: string;
  slug: string;
  title: string;
  description: string;
  thumbnail: string;
  cardColor: string;
  price: number;
  originalPrice: number;
  priceLabel: string;
  promoEndsAt: string | null;
  conversionMode: boolean;
  landingTagline: string;
  landingColor: string;
  rating: number;
  totalLessons: number;
  freeLessonCount: number;
  firstFreeLessonId: string | null;
  studentCount: number;
}

interface Props {
  badge: string;
  title: string;
  subtitle: string;
  ctaText: string;
  stickyText: string;
  stickyCtaText: string;
  compareEnabled: boolean;
  tutorHours: number;
  tutorHourlyPrice: number;
  sliderInterval: number;
}

const fmt = (n: number) => `${Math.round(n).toLocaleString("pl-PL")} zł`;
const hasPromo = (c: LandingCourse) => c.originalPrice > 0 && c.originalPrice > c.price;
const discountPct = (c: LandingCourse) =>
  hasPromo(c) ? Math.round(((c.originalPrice - c.price) / c.originalPrice) * 100) : 0;
const taglineOf = (c: LandingCourse) =>
  c.landingTagline?.trim() || c.description?.split(/(?<=[.!?])\s/)[0]?.trim() || "Darmowe lekcje już dostępne";
const claimHref = (c: LandingCourse) => `/kurs/${c.slug}/odbierz`;
const courseHref = (c: LandingCourse) => `/kurs/${c.slug}`;
// Kolor motywu landingu: dedykowany landingColor, w razie braku — kolor karty.
const themeColor = (c: LandingCourse) => c.landingColor?.trim() || c.cardColor || "#f59e0b";

// Rotujące, pogrubione frazy sprzedażowe w hero.
const HERO_PHRASES = [
  "Zdaj maturę bez stresu",
  "Ucz się kiedy i gdzie chcesz",
  "Od podstaw aż do matury",
  "Wideo, zadania i egzaminy próbne",
  "Bez wychodzenia z domu",
];

// Pływające symbole tła — zależne od przedmiotu aktywnego slajdu.
const MATH_GLYPHS = ["√", "π", "∫", "∑", "x²", "Δ", "∞", "±", "≈", "θ", "½", "∂", "a²+b²", "f(x)", "≤", "sin"];
const POLISH_GLYPHS = ["„", "”", "ę", "ą", "ó", "ż", "ł", "…", "«", "»", "poezja", "epika", "metafora", "Aa", "?", "!"];

const DECOR_POSITIONS: { top: string; left: string; size: number }[] = [
  { top: "12%", left: "6%", size: 30 },
  { top: "22%", left: "89%", size: 26 },
  { top: "68%", left: "9%", size: 34 },
  { top: "82%", left: "84%", size: 24 },
  { top: "42%", left: "50%", size: 22 },
  { top: "8%", left: "46%", size: 20 },
  { top: "56%", left: "93%", size: 30 },
  { top: "86%", left: "42%", size: 26 },
  { top: "16%", left: "72%", size: 22 },
  { top: "48%", left: "3%", size: 24 },
  { top: "32%", left: "24%", size: 20 },
  { top: "74%", left: "62%", size: 22 },
  { top: "13%", left: "30%", size: 18 },
  { top: "62%", left: "36%", size: 18 },
];

type HeroSubject = "math" | "polish" | "generic";

function subjectOfTitle(title: string | undefined): HeroSubject {
  if (!title) return "generic";
  if (/matematy/i.test(title)) return "math";
  if (/polski|polon/i.test(title)) return "polish";
  return "generic";
}

// Pływające symbole w tle hero (wzory / cytaty), delikatne i wyważone.
function HeroDecorations({ subject, accent }: { subject: HeroSubject; accent: string }) {
  const glyphs =
    subject === "math"
      ? MATH_GLYPHS
      : subject === "polish"
      ? POLISH_GLYPHS
      : [...MATH_GLYPHS.slice(0, 8), ...POLISH_GLYPHS.slice(0, 8)];
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={subject}
        aria-hidden
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.8 }}
        className="pointer-events-none absolute inset-0 overflow-hidden"
      >
        {DECOR_POSITIONS.map((p, i) => (
          <motion.span
            key={i}
            className="absolute select-none font-semibold"
            style={{ top: p.top, left: p.left, fontSize: p.size, color: accent }}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: [0, 0.18, 0.12, 0.18], y: [0, -14, 0], rotate: [0, i % 2 ? 8 : -8, 0], scale: 1 }}
            transition={{ duration: 7 + (i % 5), repeat: Infinity, delay: i * 0.2, ease: "easeInOut" }}
          >
            {glyphs[i % glyphs.length]}
          </motion.span>
        ))}
      </motion.div>
    </AnimatePresence>
  );
}

// Rotująca, pogrubiona fraza z animowanym podkreśleniem.
function RotatingPhrase({ accent }: { accent: string }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((v) => (v + 1) % HERO_PHRASES.length), 2600);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="mt-4 flex items-center justify-center gap-2 lg:justify-start">
      <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: accent }} />
      <div className="relative h-7 overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.span
            key={i}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -20, opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="block whitespace-nowrap text-lg font-bold text-slate-800"
          >
            {HERO_PHRASES[i]}
          </motion.span>
        </AnimatePresence>
      </div>
    </div>
  );
}

// Przyciemnia kolor hex o zadany współczynnik (do gradientów akcentu).
function shade(hex: string, factor = 0.82): string {
  const h = hex.replace("#", "");
  if (h.length !== 6) return hex;
  const r = Math.round(parseInt(h.slice(0, 2), 16) * factor);
  const g = Math.round(parseInt(h.slice(2, 4), 16) * factor);
  const b = Math.round(parseInt(h.slice(4, 6), 16) * factor);
  return `#${[r, g, b].map((v) => Math.min(255, v).toString(16).padStart(2, "0")).join("")}`;
}

export default function KursyMaturalneClient({
  badge,
  title,
  subtitle,
  ctaText,
  stickyText,
  stickyCtaText,
  compareEnabled,
  tutorHours,
  tutorHourlyPrice,
  sliderInterval,
}: Props) {
  const router = useRouter();
  const [courses, setCourses] = useState<LandingCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/courses/landing")
      .then((r) => (r.ok ? r.json() : []))
      .then((data: LandingCourse[]) => {
        if (!cancelled) setCourses(Array.isArray(data) ? data : []);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (courses.length < 2 || sliderInterval <= 0) return;
    const id = setInterval(
      () => setActiveSlide((s) => (s + 1) % courses.length),
      Math.max(2, sliderInterval) * 1000
    );
    return () => clearInterval(id);
  }, [courses.length, sliderInterval]);

  const safeIndex = courses.length ? activeSlide % courses.length : 0;
  const slide = courses[safeIndex] || null;
  const accent = slide ? themeColor(slide) : "#f59e0b";
  const heroSubject = subjectOfTitle(slide?.title);

  const nsPrice = useMemo(() => {
    const paid = courses.map((c) => c.price).filter((p) => p > 0);
    return paid.length ? Math.min(...paid) : 0;
  }, [courses]);
  const tutorTotal = Math.max(0, tutorHours) * Math.max(0, tutorHourlyPrice);
  const savings = nsPrice > 0 ? tutorTotal - nsPrice : tutorTotal;

  const scrollToKursy = () =>
    document.getElementById("kursy")?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="bg-white">
      <Navbar scope="kursyMaturalne" />

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-white pt-24 pb-16 sm:pt-28 sm:pb-24">
        {/* Animowane, jasne tło zmieniające się per przedmiot */}
        <motion.div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          animate={{
            background: `radial-gradient(1100px 460px at 78% 16%, ${accent}2b, transparent 60%), linear-gradient(180deg, ${accent}0f 0%, #ffffff 55%)`,
          }}
          transition={{ duration: 0.9, ease: "easeOut" }}
        />
        <div className="pointer-events-none absolute inset-0">
          <motion.div
            className="absolute -top-24 -left-16 h-80 w-80 rounded-full blur-3xl"
            animate={{ backgroundColor: `${accent}2b` }}
            transition={{ duration: 0.9 }}
          />
          <motion.div
            className="absolute top-1/3 -right-20 h-96 w-96 rounded-full blur-3xl"
            animate={{ backgroundColor: `${accent}1f` }}
            transition={{ duration: 0.9 }}
          />
          <div
            className="absolute inset-0 opacity-60"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgba(15,23,42,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(15,23,42,0.04) 1px, transparent 1px)",
              backgroundSize: "56px 56px",
              maskImage: "radial-gradient(ellipse at center, black, transparent 75%)",
              WebkitMaskImage: "radial-gradient(ellipse at center, black, transparent 75%)",
            }}
          />
          <HeroDecorations subject={heroSubject} accent={accent} />
        </div>

        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 lg:grid-cols-2 lg:gap-16 xl:gap-24">
          {/* Lewa kolumna */}
          <div className="text-center lg:text-left">
            {badge && (
              <motion.span
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-medium"
                style={{ color: shade(accent, 0.62), backgroundColor: `${accent}14`, borderColor: `${accent}33` }}
              >
                <Sparkles className="h-4 w-4" />
                {badge}
              </motion.span>
            )}
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="mt-5 text-4xl font-semibold leading-[1.1] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl"
            >
              {title}
            </motion.h1>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.12 }}
              className="mx-auto mt-5 max-w-lg lg:mx-0"
            >
              <span className="relative inline-block text-lg text-slate-600">
                {subtitle}
                <motion.span
                  className="absolute -bottom-1.5 left-0 h-[3px] w-full rounded-full"
                  style={{ background: `linear-gradient(90deg, ${accent}, ${accent}00)`, transformOrigin: "left" }}
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ delay: 0.55, duration: 0.7, ease: "easeOut" }}
                />
              </span>
            </motion.div>

            <RotatingPhrase accent={accent} />

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.19 }}
              className="mt-8 flex flex-col items-center gap-3 sm:flex-row lg:items-start lg:justify-start"
            >
              <motion.button
                onClick={scrollToKursy}
                animate={{
                  background: `linear-gradient(135deg, ${accent}, ${shade(accent, 0.8)})`,
                  boxShadow: `0 18px 40px -14px ${accent}80`,
                }}
                transition={{ duration: 0.6 }}
                className="group relative inline-flex w-full items-center justify-center gap-2 overflow-hidden rounded-2xl px-7 py-4 text-base font-semibold text-white transition-transform hover:scale-[1.03] sm:w-auto"
              >
                <span className="relative z-10 inline-flex items-center gap-2">
                  {ctaText || "Odbierz darmowe lekcje"}
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5" />
                </span>
                {/* shine sweep */}
                <span className="pointer-events-none absolute inset-0 z-0 -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-full" />
              </motion.button>
              <Link
                href="#kursy"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToKursy();
                }}
                className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-7 py-4 text-base font-medium text-slate-700 transition-colors hover:border-slate-300 hover:bg-slate-50 sm:w-auto"
              >
                Zobacz kursy
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.28 }}
              className="mt-7 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-slate-500 lg:justify-start"
            >
              <span className="inline-flex items-center gap-1.5">
                <CreditCard className="h-4 w-4 text-emerald-500" /> Bez karty płatniczej
              </span>
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4 text-emerald-500" /> Darmowe konto
              </span>
              <span className="inline-flex items-center gap-1.5">
                <PlayCircle className="h-4 w-4 text-emerald-500" /> Dostęp od razu
              </span>
            </motion.div>
          </div>

          {/* Prawa kolumna — slider hero */}
          <motion.div
            className="relative"
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          >
            <HeroSlider
              loading={loading}
              slide={slide}
              activeSlide={safeIndex}
              slideCount={courses.length}
              intervalMs={sliderInterval > 0 ? Math.max(2, sliderInterval) * 1000 : 0}
              onDot={setActiveSlide}
              onClaim={(c) => router.push(claimHref(c))}
            />
          </motion.div>
        </div>

        {/* Wskaźnik przewijania */}
        <motion.div
          aria-hidden
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="pointer-events-none absolute inset-x-0 bottom-5 hidden justify-center lg:flex"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white/70 text-slate-400 shadow-sm backdrop-blur"
          >
            <ChevronDown className="h-5 w-5" />
          </motion.div>
        </motion.div>
      </section>

      {/* ── KAFELKI KURSÓW ───────────────────────────────────── */}
      <section id="kursy" className="mx-auto max-w-6xl scroll-mt-24 px-4 py-16 sm:py-20">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
            Wybierz swój kurs maturalny
          </h2>
          <p className="mt-3 text-lg text-slate-500">
            Zacznij od darmowych lekcji — bez zobowiązań i bez karty płatniczej.
          </p>
        </div>

        <div className="mt-12">
          {loading ? (
            <div className="grid gap-6 sm:grid-cols-2">
              {[0, 1].map((i) => (
                <div key={i} className="h-96 animate-pulse rounded-3xl bg-slate-100" />
              ))}
            </div>
          ) : courses.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 py-16 text-center">
              <GraduationCap className="mx-auto h-10 w-10 text-slate-300" />
              <p className="mt-3 font-semibold text-slate-600">Kursy pojawią się już wkrótce</p>
              <p className="mt-1 text-sm text-slate-400">
                Zajrzyj tu za chwilę — przygotowujemy dla Ciebie darmowe lekcje.
              </p>
            </div>
          ) : (
            <div className={`grid gap-6 ${courses.length === 1 ? "sm:grid-cols-1 max-w-md mx-auto" : "sm:grid-cols-2"} ${courses.length >= 3 ? "lg:grid-cols-3" : ""}`}>
              {courses.map((c, i) => (
                <CourseTile
                  key={c.id}
                  course={c}
                  index={i}
                  onOpen={() => router.push(courseHref(c))}
                  onClaim={() => router.push(claimHref(c))}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── PORÓWNANIE KOSZTÓW ───────────────────────────────── */}
      {compareEnabled && (
        <section className="bg-slate-50 py-16 sm:py-20">
          <div className="mx-auto max-w-5xl px-4">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
                Policz, ile oszczędzasz
              </h2>
              <p className="mt-3 text-lg text-slate-500">
                Tradycyjne korepetycje kosztują więcej — i zabierają więcej czasu.
              </p>
            </div>

            <div className="mt-12 grid items-stretch gap-6 md:grid-cols-2">
              {/* Korki */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                className="flex flex-col rounded-3xl border border-slate-200 bg-white p-7 shadow-sm"
              >
                <p className="text-sm font-semibold uppercase tracking-wide text-slate-400">Zwykłe korepetycje</p>
                <div className="mt-3 flex items-end gap-2">
                  <span className="text-4xl font-semibold text-slate-900">{fmt(tutorTotal)}</span>
                </div>
                <p className="mt-1 text-sm text-slate-500">
                  {tutorHours} godzin × {fmt(tutorHourlyPrice)}
                </p>
                <ul className="mt-6 space-y-3 text-sm text-slate-600">
                  {[
                    { icon: MapPin, t: "Dojazdy na zajęcia" },
                    { icon: CalendarX, t: "Szukanie terminów" },
                    { icon: Clock, t: "Ograniczona dostępność" },
                  ].map(({ icon: Icon, t }) => (
                    <li key={t} className="flex items-center gap-3">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-400">
                        <X className="h-4 w-4" />
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <Icon className="h-4 w-4 text-slate-400" /> {t}
                      </span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* NextSchool */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: 0.1 }}
                className="relative flex flex-col overflow-hidden rounded-3xl border-2 border-emerald-500 bg-gradient-to-br from-emerald-500 to-teal-600 p-7 text-white shadow-xl shadow-emerald-500/20"
              >
                <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-white/10 blur-2xl" />
                <p className="text-sm font-semibold uppercase tracking-wide text-emerald-100">NextSchool</p>
                <div className="mt-3 flex items-end gap-2">
                  <span className="text-4xl font-semibold">{nsPrice > 0 ? `od ${fmt(nsPrice)}` : "Za darmo na start"}</span>
                </div>
                <p className="mt-1 text-sm text-emerald-50/90">
                  {savings > 0 ? `Oszczędzasz nawet ${fmt(savings)}` : "Zacznij bez żadnych kosztów"}
                </p>
                <ul className="mt-6 space-y-3 text-sm">
                  {[
                    "Darmowe lekcje na start",
                    "Pełny kurs online",
                    "Nauka kiedy chcesz",
                    "Dostęp na każdym urządzeniu",
                    "Niższy koszt niż korepetycje",
                  ].map((t) => (
                    <li key={t} className="flex items-center gap-3">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/20">
                        <Check className="h-4 w-4" />
                      </span>
                      {t}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={scrollToKursy}
                  className="mt-7 inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-6 py-3.5 text-sm font-semibold text-emerald-700 shadow-lg transition-transform hover:scale-[1.02]"
                >
                  Odbierz darmowe lekcje <ArrowRight className="h-4 w-4" />
                </button>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* ── FINAL CTA ────────────────────────────────────────── */}
      <section className="bg-slate-50 py-16 sm:py-20">
        <div className="mx-auto max-w-4xl px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            animate={{ background: `linear-gradient(135deg, ${accent}, ${shade(accent, 0.78)})` }}
            transition={{ duration: 0.8 }}
            className="relative overflow-hidden rounded-3xl p-8 text-center shadow-xl sm:p-14"
          >
            <div
              className="pointer-events-none absolute inset-0 opacity-20"
              style={{ backgroundImage: "radial-gradient(circle at 2px 2px, white 1px, transparent 0)", backgroundSize: "32px 32px" }}
            />
            <div className="relative">
              <h2 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">
                Zacznij naukę do matury już dziś
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-lg text-white/90">
                Załóż darmowe konto i odbierz pierwsze lekcje. Bez karty, bez zobowiązań.
              </p>
              <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <button
                  onClick={scrollToKursy}
                  className="group inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-white px-8 py-4 text-base font-semibold text-slate-900 shadow-lg transition-transform hover:scale-[1.03] sm:w-auto"
                >
                  {ctaText || "Odbierz darmowe lekcje"}
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5" />
                </button>
                <Link
                  href="/platforma/register"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-white/40 bg-white/10 px-8 py-4 text-base font-medium text-white transition-colors hover:bg-white/20 sm:w-auto"
                >
                  Załóż darmowe konto
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer scope="kursyMaturalne" />

      <StickyCtaBar
        text={stickyText || "Nie czekaj, odbierz darmowe lekcje maturalne"}
        ctaText={stickyCtaText || "Zarejestruj się teraz"}
        ctaHref="/platforma/register"
      />
    </div>
  );
}

// ─── Slajd hero ──────────────────────────────────────────────────
function HeroSlider({
  loading,
  slide,
  activeSlide,
  slideCount,
  intervalMs,
  onDot,
  onClaim,
}: {
  loading: boolean;
  slide: LandingCourse | null;
  activeSlide: number;
  slideCount: number;
  intervalMs: number;
  onDot: (i: number) => void;
  onClaim: (c: LandingCourse) => void;
}) {
  if (loading) {
    return <div className="mx-auto h-[460px] w-full max-w-md animate-pulse rounded-[28px] bg-slate-100" />;
  }

  if (!slide) {
    // fallback bez kursów
    return (
      <div className="mx-auto flex h-[460px] w-full max-w-md flex-col items-center justify-center rounded-[28px] border border-slate-200 bg-slate-50 p-8 text-center">
        <GraduationCap className="h-12 w-12 text-amber-500" />
        <p className="mt-4 text-lg font-semibold text-slate-800">Darmowe lekcje już wkrótce</p>
        <p className="mt-2 text-sm text-slate-500">Przygotowujemy dla Ciebie kursy maturalne.</p>
      </div>
    );
  }

  const color = themeColor(slide);
  const subject = /matematy/i.test(slide.title)
    ? "Matematyka"
    : /polski|polon/i.test(slide.title)
    ? "Język polski"
    : "Kurs maturalny";

  return (
    <div className="mx-auto w-full max-w-md [perspective:1400px]">
      {/* Etykieta przedmiotu — zmienia się per slajd */}
      <div className="mb-3 flex items-center justify-center gap-2 lg:justify-start">
        <AnimatePresence mode="wait">
          <motion.span
            key={subject + slide.id}
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.3 }}
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold"
            style={{ color: shade(color, 0.6), backgroundColor: `${color}1f` }}
          >
            <BookOpen className="h-3.5 w-3.5" /> {subject}
          </motion.span>
        </AnimatePresence>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={slide.id}
          initial={{ opacity: 0, x: 48, rotateY: 10 }}
          animate={{ opacity: 1, x: 0, rotateY: 0 }}
          exit={{ opacity: 0, x: -48, rotateY: -10 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="relative overflow-hidden rounded-[28px] shadow-2xl"
          style={{ background: `linear-gradient(150deg, ${color}, ${shade(color, 0.72)})`, boxShadow: `0 30px 60px -20px ${color}66` }}
        >
          <div className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-white/20 blur-2xl" />
          <motion.div
            aria-hidden
            className="pointer-events-none absolute -left-10 bottom-4 h-28 w-28 rounded-full bg-white/10 blur-2xl"
            animate={{ scale: [1, 1.2, 1], opacity: [0.4, 0.7, 0.4] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          />

          <div className="relative p-6">
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-xs font-semibold text-white backdrop-blur">
                <Sparkles className="h-3.5 w-3.5" /> Darmowe lekcje już dostępne
              </span>
              {hasPromo(slide) && (
                <span className="rounded-full bg-white/90 px-2.5 py-1 text-xs font-semibold" style={{ color }}>
                  -{discountPct(slide)}%
                </span>
              )}
            </div>

            {slide.thumbnail ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={slide.thumbnail}
                alt={slide.title}
                className="mt-4 aspect-[16/10] w-full rounded-2xl object-cover shadow-lg"
              />
            ) : (
              <div className="mt-4 flex aspect-[16/10] w-full items-center justify-center rounded-2xl bg-white/15">
                <GraduationCap className="h-12 w-12 text-white/80" />
              </div>
            )}

            <h3 className="mt-5 text-2xl font-semibold leading-tight text-white">{slide.title}</h3>
            <p className="mt-1.5 text-sm font-medium text-white/85">{taglineOf(slide)}</p>

            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-medium text-white/85">
              <span className="inline-flex items-center gap-1"><MonitorPlay className="h-3.5 w-3.5" /> {slide.totalLessons} lekcji</span>
              {slide.freeLessonCount > 0 && (
                <span className="inline-flex items-center gap-1"><Gift className="h-3.5 w-3.5" /> {slide.freeLessonCount} za darmo</span>
              )}
              {slide.rating > 0 && (
                <span className="inline-flex items-center gap-1"><Star className="h-3.5 w-3.5 fill-white/90" /> {slide.rating.toFixed(1)}</span>
              )}
            </div>

            <div className="mt-4 flex items-end gap-2">
              {hasPromo(slide) && (
                <span className="text-sm text-white/60 line-through">{fmt(slide.originalPrice)}</span>
              )}
              <span className="text-2xl font-semibold text-white">
                {slide.price > 0 ? fmt(slide.price) : "Za darmo"}
              </span>
              {slide.priceLabel && <span className="text-sm font-semibold text-white/70">{formatPriceLabel(slide.priceLabel)}</span>}
            </div>

            <button
              onClick={() => onClaim(slide)}
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-white px-6 py-3.5 text-sm font-semibold shadow-lg transition-transform hover:scale-[1.02]"
              style={{ color }}
            >
              <Gift className="h-4 w-4" /> Odbierz darmowe lekcje
            </button>
          </div>

          {/* pasek auto-zmiany slajdu */}
          {intervalMs > 0 && slideCount > 1 && (
            <div className="absolute inset-x-0 bottom-0 h-1 bg-white/15">
              <motion.div
                key={slide.id}
                className="h-full bg-white/70"
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ duration: intervalMs / 1000, ease: "linear" }}
              />
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {slideCount > 1 && (
        <div className="mt-5 flex items-center justify-center gap-2">
          {Array.from({ length: slideCount }).map((_, i) => (
            <button
              key={i}
              aria-label={`Slajd ${i + 1}`}
              onClick={() => onDot(i)}
              className={`h-2 rounded-full transition-all ${
                i === activeSlide ? "w-7" : "w-2 bg-slate-300 hover:bg-slate-400"
              }`}
              style={i === activeSlide ? { backgroundColor: color } : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Kafelek kursu ───────────────────────────────────────────────
function CourseTile({
  course,
  index,
  onOpen,
  onClaim,
}: {
  course: LandingCourse;
  index: number;
  onOpen: () => void;
  onClaim: () => void;
}) {
  const color = themeColor(course);
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ delay: index * 0.08 }}
      whileHover={{ y: -6 }}
      onClick={onOpen}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") onOpen();
      }}
      className="group flex cursor-pointer flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-xl"
    >
      {/* Grafika */}
      <div className="relative aspect-[16/9] overflow-hidden" style={{ backgroundColor: `${color}1a` }}>
        {course.thumbnail ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={course.thumbnail}
            alt={course.title}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <GraduationCap className="h-12 w-12" style={{ color }} />
          </div>
        )}
        <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1 text-xs font-semibold text-emerald-700 shadow-sm backdrop-blur">
          <Sparkles className="h-3.5 w-3.5" /> Darmowe lekcje już dostępne
        </span>
        {hasPromo(course) && (
          <span
            className="absolute right-3 top-3 rounded-full px-2.5 py-1 text-xs font-semibold text-white shadow-sm"
            style={{ backgroundColor: color }}
          >
            -{discountPct(course)}%
          </span>
        )}
      </div>

      {/* Treść */}
      <div className="flex flex-1 flex-col p-5">
        <h3 className="text-lg font-semibold leading-snug text-slate-900">{course.title}</h3>
        <p className="mt-1.5 line-clamp-2 text-sm text-slate-500">{taglineOf(course)}</p>

        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1">
            <MonitorPlay className="h-3.5 w-3.5" style={{ color }} /> {course.totalLessons} lekcji
          </span>
          {course.freeLessonCount > 0 && (
            <span className="inline-flex items-center gap-1">
              <Gift className="h-3.5 w-3.5 text-emerald-500" /> {course.freeLessonCount} za darmo
            </span>
          )}
          {course.rating > 0 && (
            <span className="inline-flex items-center gap-1">
              <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> {course.rating.toFixed(1)}
            </span>
          )}
        </div>

        <PromoCountdown endsAt={course.promoEndsAt} variant="compact" className="mt-3" />

        {/* Cena */}
        <div className="mt-4 flex items-end gap-2">
          {hasPromo(course) && (
            <span className="text-sm text-slate-400 line-through">{fmt(course.originalPrice)}</span>
          )}
          <span className="text-2xl font-semibold text-slate-900">
            {course.price > 0 ? fmt(course.price) : "Za darmo"}
          </span>
          {course.priceLabel && (
            <span className="text-sm font-semibold text-slate-500">{formatPriceLabel(course.priceLabel)}</span>
          )}
        </div>

        {/* CTA */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClaim();
          }}
          className="mt-5 inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3.5 text-sm font-semibold text-white shadow-lg transition-transform hover:scale-[1.02]"
          style={{ backgroundColor: color }}
        >
          <Gift className="h-4 w-4" /> Odbierz darmowe lekcje
        </button>
        <span className="mt-2 text-center text-xs text-slate-400 transition-colors group-hover:text-slate-500">
          Kliknij, aby zobaczyć szczegóły kursu
        </span>
      </div>
    </motion.div>
  );
}
